import React from 'react'

import { Container, styled } from '@mui/material'
import { useLocation } from 'react-router-dom'

import useDomRectObserver from '../Hooks/useDomRectObserver'
import Nav from '../Nav'
import ContentContainer from '../ContentContainer'
import WelcomeBanner from '../WelcomeBanner'
import Footer from './Footer'
import { AccessoryDrawer } from '../../spark_libs_deprecated/feature-dashboards'
import { DRAWER_WIDTH } from '../SideDrawer/DesktopDrawer'
import { useDrawerOpenStore } from '../SideDrawer/DesktopDrawer/drawerOpen.store'
import { useIsAuthenticated } from '@edge/spark/io-aws'
import { useFullWidthUi } from '@edge/spark/io-roles'
import { useAccessoryDrawerWidth } from '@edge/spark/io-dashboards'

interface FooterProps {
    footerHeight: number
}

interface DrawerExpanded {
    leftDrawerExpanded: boolean
    rightDrawerSize: number
}

interface ViewContainerProps extends FooterProps, DrawerExpanded {}

const ViewContainer = styled('div')<ViewContainerProps>`
    padding: 16px 0 16px 64px;
    @media (max-width: 599px) {
        padding-left: 0;
        padding-right: 0;
    }

    // 100vh - header - footer - view container padding
    min-height: ${({ footerHeight }): string => `calc(100vh - ${64 + footerHeight + 16 + 16}px)`};

    @media (max-width: 600px) {
        min-height: ${({ footerHeight }): string => `calc(100vh - ${56 + footerHeight + 16 + 16}px)`};
    }

    @media (min-width: 1024px) and (max-width: 1600px) {
        ${({ leftDrawerExpanded }): string =>
            leftDrawerExpanded ? `margin-left: ${DRAWER_WIDTH}px;` : `margin-left: 64px;`}
        transition: margin-left 0.2s ease;
    }
    @media (min-width: 768px) and (max-width: 1024px) {
        ${({ leftDrawerExpanded }): string => (leftDrawerExpanded ? `margin-left: ${DRAWER_WIDTH}px;` : '')}
        transition: margin-left 0.2s ease;
    }
`

const LoginView = styled('div')<FooterProps>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: ${({ footerHeight }): string => `calc(100vh - ${footerHeight}px)`};
`

interface Props {
    children: React.ReactNode
}

const noNavPaths = ['/login', '/forgotpassword', '/forcechangepassword', '/reports']

const Layout = ({ children }: Props): React.ReactElement => {
    const isAuthenticated = useIsAuthenticated()
    const { open: gloablDrawerOpenState } = useDrawerOpenStore()
    const { pathname } = useLocation()
    const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname
    const showNav = noNavPaths.find((path) => path === normalizedPathname) === undefined
    const footerRef = React.useRef<HTMLDivElement | null>(null)
    const footerHeight = useDomRectObserver(footerRef.current)?.height ?? 32
    const accessoryDrawerWidth = useAccessoryDrawerWidth()

    const fullWidth = useFullWidthUi()

    if (!children) {
        throw new Error('Missing Layout `children`')
    }

    return (
        <>
            {showNav && <Nav />}
            {isAuthenticated ? (
                <>
                    <AccessoryDrawer />
                    <ContentContainer>
                        <ViewContainer
                            footerHeight={footerHeight}
                            leftDrawerExpanded={gloablDrawerOpenState}
                            rightDrawerSize={accessoryDrawerWidth}
                        >
                            <Container
                                sx={{
                                    ...(fullWidth
                                        ? {
                                              maxWidth: `calc(100% - ${
                                                  gloablDrawerOpenState ? DRAWER_WIDTH - 64 : 0
                                              }px) !important`,
                                              marginRight: 0,
                                          }
                                        : {}),
                                }}
                            >
                                {children}
                            </Container>
                        </ViewContainer>
                    </ContentContainer>
                </>
            ) : (
                <LoginView footerHeight={footerHeight}>
                    <Container>
                        <WelcomeBanner />
                        <Container disableGutters maxWidth="xs">
                            {children}
                        </Container>
                    </Container>
                </LoginView>
            )}
            <Footer footerRef={footerRef} />
        </>
    )
}

export default Layout
